import { useState } from "react";

import { makeGetApiCall, makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";

import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../../components/modals/EmptyModal";
import type { SlideOverState } from "../../../components/slideOver/SlideOver";
import type { SelectOption } from "../../../types/api";
import type { CustomerMutationResponse, CustomerRecord, TaxRateOption, UpdateCustomerPayload } from "../../../types/customers";

export function CustomerDetailsViewModel(logout: () => void, customerId: string) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<CustomerRecord | null>(null);
  const [customerGroupOptions, setCustomerGroupOptions] = useState<SelectOption[]>([]);
  const [usersOptions, setUsersOptions] = useState<SelectOption[]>([]);
  const [taxRateOptions, setTaxRateOptions] = useState<TaxRateOption[]>([]);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getCustomer = async function (search: string | null = null, page = 1) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<CustomerRecord, typeof payload>(`customers/${customerId}`, payload);
    if (callResult.success) {
      setCustomer(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch customer");
    }
    setLoading(false);
  };

  const updateCustomer = async function (values: UpdateCustomerPayload) {
    if (!customer) return;
    const callResult = await makePutApiCall<CustomerMutationResponse, UpdateCustomerPayload>(`customers/${customer.id}`, values);

    if (callResult.success) {
      showSuccess(callResult.data.message || "Customer updated");
      setSlideOver({
        childComponent: null,
        open: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not update customer");
    }
  };

  const getCustomerGroupsForDropdown = async function () {
    const callResult = await makePostApiCall<SelectOption[]>(`getCustomersGroupsForDropdown`);
    if (callResult.success) {
      setCustomerGroupOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch customer groups");
    }
  };

  const getUsersForDropdown = async function () {
    const callResult = await makePostApiCall<SelectOption[]>(`usersForDropdown`);
    if (callResult.success) {
      setUsersOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch users");
    }
  };

  const getTaxRatesForDropdown = async function () {
    const callResult = await makeGetApiCall<TaxRateOption[]>("tax-rates/dropdown");
    if (callResult.success) setTaxRateOptions(callResult.data);
    else showErrorModal("Could not fetch tax rates");
  };

  return {
    customerGroupOptions,
    getCustomerGroupsForDropdown,
    updateCustomer,
    getCustomer,
    slideOver,
    setSlideOver,
    customer,
    loading,
    emptyModal,
    setEmptyModelOpen,
    getUsersForDropdown,
    usersOptions,
    taxRateOptions,
    getTaxRatesForDropdown,
  };
}
